//* Libraries imports
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TechinicalOutputDTO, MappingRule } from "../techinicaldocument/types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

//*Component imports
import { TechnicalPieceForm } from "./TechnicalPieceForm";
import { RulesSection } from "./RulesSection";

//* Hooks imports
import { useGenerateDocument } from "@/hooks/techinicals-documents/mutations/useGenerateDocument";
import { useSaveConfig } from "../../hooks/techinicals-documents/mutations/useSaveConfig";

//* Service imports
import { techinicalService } from "@/features/techinicaldocument/TechinicalService";
import { fetchPhaseForms } from "@/features/phaseForm/phaseFormSlice";

const mappingRuleItemSchema = z.object({
  key: z.string(),
  value: z.string(),
});

const mappingRuleConfigSchema = z.object({
  value: z.string().optional(),
  field: z
    .enum(["name", "cpf", "email", "whatsapp", "full_address"])
    .optional(),
  fieldKey: z.string().optional(),
  fieldId: z.string().optional(),
  fieldType: z.string().optional(),
  label: z.string().optional(),
  phaseFormId: z.string().optional(),
  productId: z.string().optional(),
  items: z.array(mappingRuleItemSchema).optional(),
});

const mappingRuleSchema = z.object({
  tag: z.string(),
  type: z.enum(["fixed", "client", "form"]),
  config: mappingRuleConfigSchema,
});

const generateSchema = z.object({
  replacements: z.record(z.string()),
  mappings: z.record(mappingRuleSchema),
  baseForm: z.string().optional(),
});

type GenerateFormSchema = z.infer<typeof generateSchema>;

type MainContentProps = {
  data: TechinicalOutputDTO | null;
};

export const MainContent = ({ data }: MainContentProps) => {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const { handleSubmit, setValue, watch } = useForm<GenerateFormSchema>({
    resolver: zodResolver(generateSchema),
    defaultValues: {
      replacements: {},
      mappings: {},
      baseForm: "",
    },
  });

  const replacements = watch("replacements");
  const mappings = watch("mappings");
  const selectedPhaseFormId = watch("baseForm");

  const navigate = useNavigate();
  const { generate, loading: generating } = useGenerateDocument();
  const { saveConfig, loading: saving } = useSaveConfig();
  const dispatch = useDispatch<AppDispatch>();
  const phaseForms = useSelector((s: RootState) => s.phaseForm.items);

  const loading = generating || saving;

  useEffect(() => {
    const loadConfig = async () => {
      if (!data?._id) return;
      const resp = await techinicalService.getConfig(data._id);
      if ("data" in resp && resp.data?.config) {
        const cfg = resp.data.config;

        if (cfg.baseForm) {
          setValue("baseForm", cfg.baseForm);
        }

        if (cfg?.mappings && cfg.mappings.length > 0) {
          const initialReplacements: Record<string, string> = {};
          const initialMappings: Record<string, MappingRule> = {};

          cfg.mappings.forEach((m) => {
            initialMappings[m.tag] = m;
            if (m.type === "fixed" && m.config?.value) {
              initialReplacements[m.tag] = m.config.value;
            }
          });

          setValue("replacements", initialReplacements);
          setValue("mappings", initialMappings);
        }
      }
    };
    loadConfig();
  }, [data?._id, setValue]);

  useEffect(() => {
    if (!phaseForms || phaseForms.length === 0) {
      dispatch(fetchPhaseForms());
    }
  }, [dispatch, phaseForms?.length]);

  const handleTest = async (formData: GenerateFormSchema) => {
    if (!data?._id) return;
    try {
      const result = await generate(data._id, {
        replacements: formData.replacements,
        mappingRules: Object.values(formData.mappings),
        baseForm: formData.baseForm,
      });
      if (result && result.url) {
        window.open(result.url, "_blank");
      }
    } catch (e) {
      console.error("Error generating test document:", e);
      alert("Erro ao gerar documento de teste. Tente novamente.");
    }
  };

  const handleSaveAndGenerate = async (formData: GenerateFormSchema) => {
    if (!data?._id) return;
    try {
      // 1. Salvar configurações
      await saveConfig(data._id, {
        mappings: Object.values(formData.mappings),
        baseForm: formData.baseForm,
      });

      // 2. Gerar documento
      await generate(data._id, {
        replacements: formData.replacements,
        mappingRules: Object.values(formData.mappings),
        baseForm: formData.baseForm,
      });

      navigate(-1);
    } catch (e) {
      console.error("Error saving and generating document:", e);
      alert("Erro ao salvar e gerar documento. Tente novamente.");
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full h-full bg-background-dark p-6 max-md:p-4">
      <div className="flex-1 w-full h-full">
        <section className="relative flex flex-col w-full h-full bg-zinc-100 rounded-2xl overflow-hidden">
          <div className="flex-1 w-full p-5 overflow-y-auto">
            <div className="flex flex-col lg:flex-row gap-5 w-full h-full">
              <TechnicalPieceForm
                data={data}
                selectedTag={selectedTag}
                onTagSelect={setSelectedTag}
                replacements={replacements}
                mappings={mappings}
              />
              <RulesSection
                selectedTag={selectedTag}
                replacements={replacements}
                onReplacementsChange={(val) => setValue("replacements", val)}
                mappings={mappings}
                onMappingsChange={(val) => setValue("mappings", val)}
                phaseFormId={selectedPhaseFormId || undefined}
                phaseForms={phaseForms}
                onPhaseFormChange={(val) => setValue("baseForm", val)}
              />
            </div>
          </div>
          <button
            onPointerDown={() => navigate(-1)}
            className="absolute right-5 top-5 z-10 p-2 size-10 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <img
              src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/78d465569b63bcf708a3f4d11bca2270357793e9?placeholderIfAbsent=true"
              alt="Close"
              className="size-full object-contain opacity-60"
            />
          </button>
          <div className="flex gap-3 items-center px-5 py-4 bg-white border-t border-gray-200">
            <div className="flex-1 flex gap-3 items-center"></div>
            <div className="flex gap-3">
              <button
                onClick={handleSubmit(handleTest)}
                disabled={loading}
                className={`px-8 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {generating ? "Gerando..." : "Testar"}
              </button>
              <button
                onClick={handleSubmit(handleSaveAndGenerate)}
                disabled={loading}
                className={`px-8 py-3 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
